/**
 * Broker connection: which StockIntel credentials are stored (never shown),
 * connect / one-time code, quota, and the account's equity.
 *
 * The token and PIN are write-only: they go to the server once, are stored
 * encrypted, and no endpoint returns them. The inputs are cleared on save.
 */
import { useEffect, useState } from 'react';
import { Button, Card } from '../../components/UI';
import {
  connectBroker,
  disconnectBroker,
  getAccount,
  removeConnection,
  saveConnection,
  selectConnection,
  submitOtp,
  type AccountView,
  type BotEnv,
  type ConnectionView,
} from '../../lib/bots';
import { useConfirm } from '../../components/Confirm';
import {
  CardHead,
  EnvPill,
  ErrorLine,
  Field,
  Kpi,
  Meter,
  Notice,
  Pill,
  fmt,
  inputStyle,
  mono,
  pktTime,
  signed,
  pnlColor,
  usePoll,
} from './ui';

const STATE_COLOR: Record<string, string> = {
  ready: 'var(--tk-up)',
  otp_required: 'var(--tk-warn)',
  connecting: 'var(--tk-warn)',
  reconnecting: 'var(--tk-warn)',
  refused: 'var(--tk-down)',
  superseded: 'var(--tk-down)',
};

interface Props {
  view: ConnectionView | null;
  onChange: (view: ConnectionView) => void;
  hasWorkingBots: boolean;
}

export const ConnectionPanel = ({ view, onChange, hasWorkingBots }: Props) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [editing, setEditing] = useState(false);

  const run = async (fn: () => Promise<ConnectionView>) => {
    setBusy(true);
    setError(null);
    try {
      onChange(await fn());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const status = view?.status ?? null;
  const state = status?.state ?? 'idle';
  const stored = view?.connections ?? [];
  const selected = stored.find((c) => c.selected);
  const budget = status?.budget;

  return (
    <Card padding={24}>
      <CardHead
        title="Broker"
        right={
          view?.engine?.alive ? `engine ok · ${view.engine.active} active` : 'engine not reporting'
        }
      />

      {view && !view.engine?.alive && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="down">
            The bot engine is not running. Bots cannot trade until it is back.
          </Notice>
        </div>
      )}
      {view && !view.secretsConfigured && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="down">
            SECRETS_KEY is not set on the server, so credentials cannot be stored. See
            apps/api/.env.example.
          </Notice>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {selected ? (
          <EnvPill env={selected.env} />
        ) : (
          <Pill color="var(--tk-ink-3)">no credentials</Pill>
        )}
        {selected && (
          <Pill color={STATE_COLOR[state] ?? 'var(--tk-ink-3)'}>{state.replace('_', ' ')}</Pill>
        )}
        {status?.account && (
          <span style={{ fontSize: 12, color: 'var(--tk-ink-2)', ...mono }}>
            {status.account.broker} / {status.account.client}
          </span>
        )}
        {status?.sessionStatus && state === 'ready' && (
          <span style={{ fontSize: 12, color: 'var(--tk-ink-3)', ...mono }}>
            market {status.sessionStatus}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {selected && state !== 'ready' && state !== 'otp_required' && state !== 'connecting' && (
          <Button size="sm" disabled={busy} onClick={() => run(connectBroker)}>
            Connect
          </Button>
        )}
        {selected && (state === 'ready' || state === 'otp_required') && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy || hasWorkingBots}
            onClick={() => run(disconnectBroker)}
          >
            Disconnect
          </Button>
        )}
        <Button size="sm" variant="subtle" onClick={() => setEditing((v) => !v)}>
          {editing ? 'Close' : stored.length ? 'Credentials' : 'Add credentials'}
        </Button>
      </div>

      {state === 'superseded' && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="down">
            Another program connected with this token, and StockIntel allows one connection per
            token. This one will not reconnect by itself. Close the other program first, then press
            Connect.
          </Notice>
        </div>
      )}
      {status?.lastError && state !== 'ready' && state !== 'superseded' && (
        <div style={{ marginTop: 10 }}>
          <ErrorLine>{status.lastError}</ErrorLine>
        </div>
      )}
      {selected?.env === 'live' && status && !status.liveTradingEnabled && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="warn">
            Live trading is switched off on this server (SI_LIVE_TRADING). The connection works for
            reading the account, but no order will be sent.
          </Notice>
        </div>
      )}

      {state === 'otp_required' && (
        <form
          style={{
            marginTop: 14,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
          onSubmit={(e) => {
            e.preventDefault();
            const code = otp.trim();
            setOtp('');
            void run(() => submitOtp(code));
          }}
        >
          <div style={{ width: 200 }}>
            <Field
              label="One-time code"
              hint={
                status?.otpMessage ||
                (status?.otpHasEmail ? 'Emailed by StockIntel, valid for 7 days.' : undefined)
              }
            >
              <input
                style={inputStyle}
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="······"
              />
            </Field>
          </div>
          <Button size="sm" type="submit" disabled={busy || !/^\d{4,8}$/.test(otp.trim())}>
            Submit code
          </Button>
        </form>
      )}

      {state === 'ready' && budget && (
        <div style={{ marginTop: 20, maxWidth: 420 }}>
          <Meter
            label="Orders today"
            used={budget.used}
            limit={budget.limit}
            hint={`Orders and cancels on ${budget.day}. Plan: ${Number(status?.quotas?.orders_per_day ?? 0)} a day, ${status?.quotas?.good_till_cancelled ? 'GTC available' : 'day orders only'}. Connected ${pktTime(status?.connectedAt ?? null)} PKT.`}
          />
        </div>
      )}

      {state === 'ready' && <EquityStrip />}

      {editing && (
        <CredentialsForm
          stored={stored}
          locked={hasWorkingBots}
          busy={busy}
          onSave={(body) => run(() => saveConnection(body)).then(() => setEditing(false))}
          onSelect={(env) => run(() => selectConnection(env))}
          onRemove={(env) => run(() => removeConnection(env))}
        />
      )}
      {error && (
        <div style={{ marginTop: 10 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>
      )}
    </Card>
  );
};

/** Equity = buying power + money reserved in open buys + holdings at market. Settled cash does not move intraday. */
const EquityStrip = () => {
  const [account, setAccount] = useState<AccountView | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * How long the FIRST load has been running. A skeleton that shimmers for
   * ever is indistinguishable from one that is about to resolve, and on
   * 22 Sep 2026 Chase's read endpoints began timing out after 20 seconds —
   * so the panel sat there looking merely slow while nothing was coming.
   * After a few seconds it should say so.
   */
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (account) return;
    const timer = setTimeout(() => setSlow(true), 4000);
    return () => clearTimeout(timer);
  }, [account]);

  // The account snapshot is a metered read and is cached upstream for minutes anyway.
  usePoll(async () => {
    try {
      setAccount(await getAccount());
      setError(null);
      setSlow(false);
    } catch (e) {
      setError((e as Error).message);
    }
  }, 60_000);

  if (!account && error)
    return (
      <div style={{ marginTop: 12 }}>
        <ErrorLine>{error}</ErrorLine>
      </div>
    );
  /**
   * The strip keeps its space from the first paint, before the figures exist.
   *
   * It used to render nothing at all until the account arrived a second or two
   * later, and then push everything below it down the page — which is exactly
   * when someone is reaching for a Stop button. Reserving the room costs
   * nothing and the layout never moves.
   */
  return (
    <div
      title={
        account
          ? `${account.equityBasis} · broker snapshot ${pktTime(account.snapshotAt)} PKT${account.today ? ` · today bought ${fmt(account.today.bought)}, sold ${fmt(account.today.sold)}` : ''}`
          : 'Loading the broker snapshot…'
      }
      style={{
        marginTop: 22,
        paddingTop: 22,
        borderTop: '1px solid var(--tk-line-2)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 20,
      }}
    >
      <Kpi
        label="Equity"
        size={22}
        value={account ? fmt(account.equity) : <Skeleton wide slow={slow} />}
      >
        {/* Said once, under the headline figure, rather than five times across
            the row: the broker is not answering, and this is not a slow paint. */}
        {!account && slow ? (
          <span style={{ fontSize: 10.5, color: 'var(--tk-warn)', lineHeight: 1.3 }}>
            {error ? 'the broker is not answering — retrying' : 'waiting for the broker…'}
          </span>
        ) : null}
      </Kpi>
      <Kpi
        label="Buying power"
        value={account ? fmt(account.buyingPower) : <Skeleton slow={slow} />}
      />
      <Kpi
        label="In open buys"
        value={account ? fmt(account.reservedInOpenBuys) : <Skeleton slow={slow} />}
      />
      <Kpi label="Holdings" value={account ? fmt(account.holdings) : <Skeleton slow={slow} />}>
        {/* Held even when empty: the figure below Holdings is the only thing
            under any of the five, and without it the row grows on load. */}
        <span
          style={{
            fontSize: 11.5,
            minHeight: '1.2em',
            color: account ? pnlColor(account.unrealised) : 'transparent',
            ...mono,
          }}
        >
          {account ? signed(account.unrealised) : '—'}
        </span>
      </Kpi>
      <Kpi
        label="Settled cash"
        hint="Moves at T+1, not intraday"
        value={account ? fmt(account.settledCash) : <Skeleton slow={slow} />}
      />
    </div>
  );
};

/** A figure that has not arrived yet, occupying the room the figure will need. */
const Skeleton = ({ wide, slow }: { wide?: boolean; slow?: boolean }) => (
  <span
    // It stops pulsing once it is clear nothing is arriving: a bar that keeps
    // breathing reads as progress, and there is none.
    className={slow ? undefined : 'tkb-pulse'}
    aria-label={slow ? 'Waiting for the broker' : 'Loading'}
    style={{
      display: 'inline-block',
      width: wide ? '7.5em' : '6em',
      maxWidth: '100%',
      height: '0.72em',
      borderRadius: 3,
      background: 'var(--tk-line)',
      opacity: slow ? 0.45 : 1,
      verticalAlign: 'middle',
    }}
  />
);

interface FormProps {
  stored: ConnectionView['connections'];
  locked: boolean;
  busy: boolean;
  onSave: (body: { env: BotEnv; token: string; pin: string; account?: string }) => Promise<void>;
  onSelect: (env: BotEnv) => void;
  onRemove: (env: BotEnv) => void;
}

const CredentialsForm = ({ stored, locked, busy, onSave, onSelect, onRemove }: FormProps) => {
  const { ask, dialog } = useConfirm();
  const [env, setEnv] = useState<BotEnv>('sandbox');
  const [token, setToken] = useState('');
  const [pin, setPin] = useState('');
  const [account, setAccount] = useState('');
  const [ack, setAck] = useState(false);

  const canSave =
    token.trim().length >= 16 &&
    pin.trim().length >= 3 &&
    (env === 'sandbox' || ack) &&
    !locked &&
    !busy;

  return (
    <>
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid var(--tk-line-2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {locked && (
          <Notice tone="warn">
            Stop every bot before changing credentials or switching environment.
          </Notice>
        )}

        {stored.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {stored.map((c) => (
              <div
                key={c.env}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  fontSize: 12.5,
                  ...mono,
                }}
              >
                <EnvPill env={c.env} />
                <span style={{ color: 'var(--tk-ink-2)' }}>token ····{c.tokenHint}</span>
                {c.account && <span style={{ color: 'var(--tk-ink-3)' }}>{c.account}</span>}
                {c.selected ? <Pill color="var(--tk-up)">in use</Pill> : null}
                <div style={{ flex: 1 }} />
                {!c.selected && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={locked || busy}
                    onClick={() => onSelect(c.env)}
                  >
                    Use this
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="down"
                  disabled={locked || busy}
                  onClick={() =>
                    ask(
                      {
                        title: `Remove the stored ${c.env} credentials?`,
                        body: (
                          <p style={{ margin: 0 }}>
                            The token and PIN are deleted from the server. Bots on this environment
                            cannot trade until credentials are added again.
                          </p>
                        ),
                        confirmLabel: 'Remove',
                      },
                      () => onRemove(c.env),
                    )
                  }
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}

        <form
          autoComplete="off"
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSave) return;
            const body = {
              env,
              token: token.trim(),
              pin: pin.trim(),
              account: account.trim() || undefined,
            };
            // Cleared before the request leaves, whatever happens to it.
            setToken('');
            setPin('');
            setAck(false);
            void onSave(body);
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 12,
            }}
          >
            <Field label="Environment">
              <select
                style={inputStyle}
                value={env}
                onChange={(e) => setEnv(e.target.value as BotEnv)}
              >
                <option value="sandbox">sandbox — no real orders</option>
                <option value="live">live — real orders, real money</option>
              </select>
            </Field>
            <Field label="API token">
              <input
                style={inputStyle}
                type="password"
                autoComplete="new-password"
                spellCheck={false}
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
            </Field>
            <Field label="Trading PIN">
              <input
                style={inputStyle}
                type="password"
                autoComplete="new-password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
              />
            </Field>
            <Field label="Account (optional)">
              <input
                style={inputStyle}
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                placeholder="chase/PL254"
              />
            </Field>
          </div>
          {env === 'live' && (
            <label
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                fontSize: 12.5,
                color: 'var(--tk-ink-2)',
                lineHeight: 1.5,
              }}
            >
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                I understand StockIntel allows <b>one connection per token</b>: connecting here
                disconnects any other program using this live token (it will stop receiving fills),
                and bots started on this connection place real orders.
              </span>
            </label>
          )}
          <div>
            <Button size="sm" type="submit" disabled={!canSave}>
              Save {env} credentials
            </Button>
          </div>
        </form>
      </div>
      {dialog}
    </>
  );
};
